import type { DashboardStatistics, EvidenceView, RequestTraceView, WorkspaceEntity } from "@/domain/types";

const now = Date.now();
const minutesAgo = (minutes: number) => new Date(now - minutes * 60_000).toISOString();
const daysAgo = (days: number) => new Date(now - days * 86_400_000).toISOString();

const evidence = (source: string, summary: string, weight: number, family = "network"): EvidenceView => ({
  family,
  source,
  direction: weight > 0 ? "positive" : weight < 0 ? "negative" : "neutral",
  weight,
  summary,
  observedAt: minutesAgo(90)
});

export const demoEntities: WorkspaceEntity[] = [
  {
    id: "acc-voestalpine",
    kind: "account",
    mode: "account",
    organizationId: "org-voestalpine",
    companyName: "voestalpine AG",
    legalName: "voestalpine AG",
    domain: "voestalpine.com",
    legalEntityKind: "corporation",
    employeeBand: "250+",
    consent: { status: "not_granted", purposes: [] },
    confidence: 0.992,
    confidenceLabel: "verified",
    live: true,
    firstSeenAt: daysAgo(44),
    lastSeenAt: minutesAgo(1),
    intentCategory: "Enterprise Lösungen",
    activityLevel: "high",
    sessions: 18,
    targetAccount: true,
    crmStatus: "Open opportunity",
    reviewStatus: "clean",
    location: { label: "Linz, Österreich (Netzwerkregion)", countryCode: "AT", lat: 48.30, lng: 14.29, precision: "city", radiusKm: 15 },
    companyLocation: { label: "voestalpine-Straße 1, 4020 Linz", countryCode: "AT", lat: 48.2899, lng: 14.3255, precision: "registered_office", radiusKm: 1 },
    address: { street: "voestalpine-Straße 1", postalCode: "4020", city: "Linz", countryCode: "AT" },
    journey: [
      { id: "ev-a1", occurredAt: minutesAgo(1), type: "intent_bucket", title: "Enterprise Lösungen", category: "Enterprise Lösungen" },
      { id: "ev-a2", occurredAt: minutesAgo(16), type: "intent_bucket", title: "Integrationen", category: "Integrationen" }
    ],
    evidence: [
      evidence("RIPE RDAP", "Delegated corporate prefix matches the legal entity.", 0.96),
      evidence("RPKI", "Route authorization links the prefix to the verified network owner.", 0.91),
      evidence("DNS + CT", "Corporate domains and certificates converge on the same entity.", 0.87, "domain")
    ],
    network: { asn: 21013, asnName: "ITandTEL", directOwner: "ITandTEL", delegatedCustomer: "voestalpine AG", prefix: "193.186.184.0/22", networkClass: "corporate" },
    resolutionStory: [
      { label: "Edge lookup", detail: "Longest-prefix match resolved a delegated business network.", status: "complete" },
      { label: "Entity graph", detail: "Registry, DNS and network evidence agree.", status: "complete" },
      { label: "Publish gate", detail: "0.992 calibrated confidence; legal entity verified.", status: "complete" }
    ]
  },
  {
    id: "visitor-palfinger",
    kind: "visitor",
    mode: "consented_visitor",
    visitorLabel: "Visitor A3F9",
    organizationId: "org-palfinger",
    companyName: "PALFINGER AG",
    legalName: "PALFINGER AG",
    domain: "palfinger.com",
    legalEntityKind: "corporation",
    employeeBand: "250+",
    consent: {
      status: "accepted",
      occurredAt: minutesAgo(31),
      expiresAt: new Date(now + 160 * 86_400_000).toISOString(),
      purposes: ["visitor_identification", "journey", "device_matching"],
      cmpSource: "Usercentrics",
      policyVersion: "2026-07"
    },
    confidence: 0.987,
    confidenceLabel: "very_high",
    live: true,
    firstSeenAt: daysAgo(21),
    lastSeenAt: minutesAgo(2),
    currentPath: "/pricing/enterprise",
    intentCategory: "Preise",
    activityLevel: "high",
    sessions: 7,
    engagementMs: 528000,
    deviceClass: "Desktop · Chrome · Windows",
    acquisition: "LinkedIn / paid",
    revealedEmail: "procurement@palfinger.com",
    targetAccount: true,
    crmStatus: "Qualified",
    reviewStatus: "clean",
    location: { label: "Wien, Österreich (Netzwerkregion)", countryCode: "AT", lat: 48.21, lng: 16.37, precision: "city", radiusKm: 15 },
    companyLocation: { label: "Lamprechtshausener Bundesstraße 8, 5101 Bergheim", countryCode: "AT", lat: 47.8406, lng: 13.0265, precision: "registered_office", radiusKm: 1 },
    address: { street: "Lamprechtshausener Bundesstraße 8", postalCode: "5101", city: "Bergheim", countryCode: "AT" },
    journey: [
      { id: "ev-p1", sessionId: "session-p1", occurredAt: minutesAgo(31), type: "page_view", title: "Landing Page", path: "/solutions", category: "Lösungen", durationMs: 92000 },
      { id: "ev-p2", sessionId: "session-p1", occurredAt: minutesAgo(20), type: "page_view", title: "Integrationen", path: "/integrations", category: "Integrationen", durationMs: 183000 },
      { id: "ev-p3", sessionId: "session-p2", occurredAt: minutesAgo(2), type: "page_view", title: "Enterprise Pricing", path: "/pricing/enterprise", category: "Preise", durationMs: 253000 }
    ],
    evidence: [
      evidence("Verified reveal", "Business email domain confirms PALFINGER AG.", 0.99, "reveal"),
      evidence("First-party identity", "Valid consented identity returned across seven sessions.", 0.94, "identity"),
      evidence("Network graph", "Corporate network is consistent with the revealed organization.", 0.83)
    ],
    network: { asn: 8447, asnName: "A1 Telekom Austria", directOwner: "A1 Telekom Austria", delegatedCustomer: "PALFINGER AG", prefix: "80.120.48.0/20", networkClass: "corporate" },
    resolutionStory: [
      { label: "Consent", detail: "Granular consent is valid for identification and journey.", status: "complete" },
      { label: "Identity", detail: "First-party token and device similarity agree.", status: "complete" },
      { label: "Reveal", detail: "Business domain confirms the organization.", status: "complete" }
    ]
  },
  {
    id: "acc-andritz",
    kind: "account",
    mode: "account",
    organizationId: "org-andritz",
    companyName: "ANDRITZ AG",
    domain: "andritz.com",
    legalEntityKind: "corporation",
    employeeBand: "250+",
    consent: { status: "not_granted", purposes: [] },
    confidence: 0.963,
    confidenceLabel: "high",
    live: true,
    firstSeenAt: daysAgo(12),
    lastSeenAt: minutesAgo(4),
    intentCategory: "Security",
    activityLevel: "medium",
    sessions: 11,
    targetAccount: false,
    reviewStatus: "candidate",
    location: { label: "Graz, Österreich (Netzwerkregion)", countryCode: "AT", lat: 47.07, lng: 15.44, precision: "city", radiusKm: 15 },
    companyLocation: { label: "Stattegger Straße 18, 8045 Graz", countryCode: "AT", lat: 47.1048, lng: 15.4157, precision: "registered_office", radiusKm: 1 },
    address: { street: "Stattegger Straße 18", postalCode: "8045", city: "Graz", countryCode: "AT" },
    journey: [{ id: "ev-an1", occurredAt: minutesAgo(4), type: "intent_bucket", title: "Security", category: "Security" }],
    evidence: [evidence("Prefix2Org", "Delegated customer is likely ANDRITZ AG.", 0.87), evidence("PeeringDB", "Network relationship corroborates the candidate.", 0.62)],
    network: { asn: 1901, asnName: "EUNET", directOwner: "A1", delegatedCustomer: "ANDRITZ AG", networkClass: "corporate" },
    resolutionStory: [
      { label: "Candidate generation", detail: "Corporate prefix produced one dominant candidate.", status: "complete" },
      { label: "Independent evidence", detail: "Second source is present but not strong enough for auto-publish.", status: "warning" }
    ]
  },
  {
    id: "acc-omv",
    kind: "account",
    mode: "account",
    organizationId: "org-omv",
    companyName: "OMV AG",
    domain: "omv.com",
    legalEntityKind: "corporation",
    employeeBand: "250+",
    consent: { status: "withdrawn", occurredAt: daysAgo(4), withdrawnAt: minutesAgo(48), purposes: ["visitor_identification"], cmpSource: "Cookiebot", policyVersion: "2026-06" },
    confidence: 0.981,
    confidenceLabel: "very_high",
    live: false,
    firstSeenAt: daysAgo(17),
    lastSeenAt: minutesAgo(48),
    intentCategory: "Case Studies",
    activityLevel: "medium",
    sessions: 3,
    engagementMs: 309000,
    targetAccount: false,
    reviewStatus: "clean",
    location: { label: "Wien, Österreich (Netzwerkregion)", countryCode: "AT", lat: 48.21, lng: 16.37, precision: "city", radiusKm: 15 },
    companyLocation: { label: "Trabrennstraße 6-8, 1020 Wien", countryCode: "AT", lat: 48.2104, lng: 16.4062, precision: "registered_office", radiusKm: 1 },
    address: { street: "Trabrennstraße 6-8", postalCode: "1020", city: "Wien", countryCode: "AT" },
    journey: [{ id: "ev-o1", occurredAt: minutesAgo(48), type: "intent_bucket", title: "Case Studies", category: "Case Studies" }],
    evidence: [evidence("Consent receipt", "Consent was withdrawn; collection stopped.", 0, "consent"), evidence("Network graph", "Corporate network resolved before withdrawal.", 0.88)],
    network: { asn: 8412, asnName: "Magenta", delegatedCustomer: "OMV AG", networkClass: "corporate" },
    resolutionStory: [{ label: "Withdrawal", detail: "Individual tracking stopped and the identity is blocked from relinking.", status: "warning" }]
  },
  {
    id: "acc-strabag",
    kind: "account",
    mode: "account",
    organizationId: "org-strabag",
    companyName: "STRABAG SE",
    domain: "strabag.com",
    legalEntityKind: "corporation",
    employeeBand: "250+",
    consent: { status: "unknown", purposes: [] },
    confidence: 0.842,
    confidenceLabel: "medium",
    live: false,
    firstSeenAt: daysAgo(3),
    lastSeenAt: minutesAgo(94),
    intentCategory: "API",
    activityLevel: "low",
    sessions: 2,
    targetAccount: false,
    reviewStatus: "review",
    location: { label: "Wien, Österreich", countryCode: "AT", lat: 48.1845, lng: 16.325, precision: "network", radiusKm: 35 },
    journey: [{ id: "ev-s1", occurredAt: minutesAgo(94), type: "intent_bucket", title: "API", category: "API" }],
    evidence: [evidence("RDAP", "Network registration contains a related entity name.", 0.58), evidence("Hosting classifier", "Shared egress lowers confidence.", -0.34, "risk")],
    network: { asn: 6830, asnName: "Liberty Global", networkClass: "provider" },
    resolutionStory: [{ label: "Publish gate", detail: "Candidate remains below the automatic threshold.", status: "warning" }]
  },
  {
    id: "acc-siemens",
    kind: "account",
    mode: "account",
    organizationId: "org-siemens",
    companyName: "Siemens AG Österreich",
    domain: "siemens.com",
    legalEntityKind: "corporation",
    employeeBand: "250+",
    consent: { status: "not_granted", purposes: [] },
    confidence: 0.995,
    confidenceLabel: "verified",
    live: false,
    firstSeenAt: daysAgo(80),
    lastSeenAt: minutesAgo(150),
    intentCategory: "Integrationen",
    activityLevel: "high",
    sessions: 29,
    targetAccount: true,
    crmStatus: "Customer",
    reviewStatus: "clean",
    location: { label: "München, Deutschland (Netzwerkregion)", countryCode: "DE", lat: 48.14, lng: 11.58, precision: "city", radiusKm: 15 },
    companyLocation: { label: "Siemensstraße 90, 1210 Wien", countryCode: "AT", lat: 48.2752, lng: 16.4196, precision: "registered_office", radiusKm: 1 },
    address: { street: "Siemensstraße 90", postalCode: "1210", city: "Wien", countryCode: "AT" },
    journey: [{ id: "ev-si1", occurredAt: minutesAgo(150), type: "intent_bucket", title: "Integrationen", category: "Integrationen" }],
    evidence: [evidence("Verified network claim", "DNS and registry verification confirm the network claim.", 1, "claim")],
    network: { asn: 20857, asnName: "Siemens AG", directOwner: "Siemens AG", delegatedCustomer: "Siemens AG Österreich", networkClass: "corporate" },
    resolutionStory: [{ label: "Verified claim", detail: "Claim overrides the standard two-source publish rule.", status: "complete" }]
  }
];

// Additional Upper-Austrian and DACH companies so the map shows region logo stacks.
const regionalDemoCompanies: Array<[string, string, string, string, number, number, number]> = [
  ["acc-keba", "KEBA Group AG", "keba.com", "Linz", 48.3164, 14.3047, 34],
  ["acc-rosenbauer", "Rosenbauer International AG", "rosenbauer.com", "Leonding", 48.2786, 14.2532, 61],
  ["acc-fronius", "Fronius International GmbH", "fronius.com", "Wels", 48.1667, 14.0333, 128],
  ["acc-greiner", "Greiner AG", "greiner.com", "Kremsmünster", 48.0525, 14.1300, 203],
  ["acc-porr", "PORR AG", "porr-group.com", "Wien", 48.1866, 16.3797, 245],
  ["acc-bechtle", "Bechtle AG", "bechtle.com", "Neckarsulm", 49.1912, 9.2253, 312]
];
const regionTemplate = demoEntities[0]!;
demoEntities.push(...regionalDemoCompanies.map(([id, companyName, domain, city, lat, lng, minutes], index): WorkspaceEntity => {
  const countryCode = city === "Neckarsulm" ? "DE" : "AT";
  return {
    ...regionTemplate,
    id, organizationId: id.replace("acc-", "org-"), companyName, legalName: companyName, domain,
    live: false, confidence: 0.9 - index * 0.03, confidenceLabel: "high",
    firstSeenAt: daysAgo(2 + index), lastSeenAt: minutesAgo(minutes), sessions: 1 + (index % 3), targetAccount: index === 1,
    location: { label: `${city} (Netzwerkregion)`, countryCode, lat: lat + 0.02, lng: lng - 0.02, precision: "city", radiusKm: 15 },
    companyLocation: { label: city, countryCode, lat, lng, precision: "registered_office", radiusKm: 1 },
    address: { city, countryCode },
    journey: [{ id: `ev-${id}`, occurredAt: minutesAgo(minutes), type: "intent_bucket", title: "Lösungen", category: "Lösungen" }]
  };
}));
// Companies on provider or mobile networks often have only a country-level origin; the map shows them vaguely.
const countryOnlyDemoCompanies: Array<[string, string, string, string, string, number]> = [
  ["acc-zf", "ZF Friedrichshafen AG", "zf.com", "DE", "Deutschland", 96],
  ["acc-continental", "Continental AG", "continental.com", "DE", "Deutschland", 151],
  ["acc-roche", "F. Hoffmann-La Roche AG", "roche.com", "CH", "Schweiz", 188]
];
demoEntities.push(...countryOnlyDemoCompanies.map(([id, companyName, domain, countryCode, country, minutes], index): WorkspaceEntity => {
  const origin = { label: `${country} (Netzwerkregion)`, countryCode, precision: "country" as const };
  return {
    ...regionTemplate,
    id, organizationId: id.replace("acc-", "org-"), companyName, legalName: companyName, domain,
    live: index === 0, confidence: 0.62 + index * 0.08, confidenceLabel: index === 0 ? "probable" : "high",
    firstSeenAt: daysAgo(1 + index), lastSeenAt: minutesAgo(minutes), sessions: 1, targetAccount: false,
    location: origin, activityOriginLocation: origin, companyLocation: undefined, address: { countryCode },
    network: { ...regionTemplate.network, networkClass: "provider" },
    journey: [{ id: `ev-${id}`, occurredAt: minutesAgo(minutes), type: "intent_bucket", title: "Preise", category: "Preise" }]
  };
}));
// Like production, account activities carry their network region separately from the company address.
for (const entity of demoEntities) {
  if (entity.companyLocation && entity.location.precision === "city" && !entity.activityOriginLocation) entity.activityOriginLocation = entity.location;
}

export const demoTraces: RequestTraceView[] = [
  {
    requestTraceId: "e81f69a5-bad3-4a94-a462-13f34eeac701",
    siteId: "moonshot-demo",
    occurredAt: minutesAgo(1),
    mode: "account",
    consentStatus: "not_granted",
    status: "identified",
    routeCategory: "Enterprise Lösungen",
    networkClass: "corporate",
    asn: 21013,
    selectedOrganization: "voestalpine AG",
    topScore: 0.992,
    pipelineStatus: "complete",
    resolverDurationMs: 3.8,
    graphReleaseId: "graph-2026-07-22.1",
    modelReleaseId: "model-2.0.0",
    connectorVersion: "edge-2.0.0",
    candidates: [{ organizationId: "org-voestalpine", name: "voestalpine AG", domain: "voestalpine.com", score: 0.992, verifiedClaim: false, evidence: [evidence("RIPE RDAP", "Delegated corporate prefix.", 0.96), evidence("RPKI", "Valid route authorization.", 0.91)], missingEvidence: [], hardRules: [] }],
    resolverSteps: [
      { name: "trusted-proxy", status: "ok", durationMs: 0.2, summary: "Direct edge address accepted." },
      { name: "radix-lookup", status: "ok", durationMs: 0.7, summary: "Longest prefix match found." },
      { name: "entity-graph", status: "ok", durationMs: 1.9, summary: "Three independent evidence families." },
      { name: "publish-gate", status: "ok", durationMs: 1, summary: "Automatic threshold satisfied." }
    ]
  },
  {
    requestTraceId: "6c0a4f46-55d5-4385-b432-345361aaf042",
    siteId: "moonshot-demo",
    occurredAt: minutesAgo(3),
    mode: "account",
    consentStatus: "unknown",
    status: "unknown",
    routeCategory: "Preise",
    networkClass: "provider",
    asn: 8447,
    topScore: 0.41,
    pipelineStatus: "complete",
    resolverDurationMs: 2.9,
    graphReleaseId: "graph-2026-07-22.1",
    modelReleaseId: "model-2.0.0",
    connectorVersion: "edge-2.0.0",
    unknownReason: "Only the access provider could be verified.",
    candidates: [
      { organizationId: "org-candidate-a", name: "Possible customer A", score: 0.41, verifiedClaim: false, evidence: [evidence("Reverse DNS", "A weak business label was found.", 0.28)], missingEvidence: ["legal entity", "domain corroboration", "delegated prefix"], hardRules: ["provider-origin must not auto-publish"] },
      { organizationId: "org-candidate-b", name: "Possible customer B", score: 0.18, verifiedClaim: false, evidence: [], missingEvidence: ["independent evidence"], hardRules: [] }
    ],
    resolverSteps: [
      { name: "radix-lookup", status: "ok", durationMs: 0.8, summary: "Provider prefix found." },
      { name: "delegated-customer", status: "miss", durationMs: 1.3, summary: "No delegated customer record." },
      { name: "publish-gate", status: "miss", durationMs: 0.8, summary: "No safe company candidate." }
    ]
  },
  {
    requestTraceId: "733351ef-2271-4b34-9927-cc2aff34ee33",
    siteId: "moonshot-demo",
    occurredAt: minutesAgo(6),
    mode: "account",
    consentStatus: "not_granted",
    status: "hosting_or_vpn",
    routeCategory: "Dokumentation",
    networkClass: "hosting",
    asn: 16509,
    topScore: 0.12,
    pipelineStatus: "complete",
    resolverDurationMs: 1.6,
    graphReleaseId: "graph-2026-07-22.1",
    modelReleaseId: "model-2.0.0",
    connectorVersion: "edge-2.0.0",
    suppressionReason: "AWS infrastructure cannot be attributed to a visiting company.",
    candidates: [{ organizationId: "org-amazon", name: "Amazon Web Services", domain: "aws.amazon.com", score: 0.12, verifiedClaim: false, evidence: [evidence("ASN", "Infrastructure owner only.", 0.2)], missingEvidence: ["end customer"], hardRules: ["hosting networks are suppressed"] }],
    resolverSteps: [
      { name: "network-classifier", status: "ok", durationMs: 0.5, summary: "Hosting network detected." },
      { name: "publish-gate", status: "skipped", durationMs: 0.1, summary: "Hard suppression applied." }
    ]
  },
  {
    requestTraceId: "e520652d-edee-4df7-aaba-17b1400283f8",
    siteId: "moonshot-demo",
    occurredAt: minutesAgo(9),
    mode: "account",
    consentStatus: "unknown",
    status: "resolver_error",
    routeCategory: "API",
    networkClass: "unknown",
    topScore: 0,
    pipelineStatus: "partial",
    resolverDurationMs: 1204,
    graphReleaseId: "graph-2026-07-22.1",
    modelReleaseId: "model-2.0.0",
    connectorVersion: "edge-2.0.0",
    unknownReason: "The regional graph shard could not be loaded.",
    candidates: [],
    resolverSteps: [
      { name: "snapshot-load", status: "timeout", durationMs: 1200, summary: "Signed shard unavailable." },
      { name: "publish-gate", status: "skipped", durationMs: 0, summary: "No resolver result." }
    ]
  }
];

const demoStatisticsTo = new Date();
const demoStatisticsFrom = new Date(demoStatisticsTo.getTime() - 29 * 86_400_000);
const demoSeriesStart = new Date(demoStatisticsTo.getTime() - 89 * 86_400_000);
export const demoTimeSeries = Array.from({ length: 90 }, (_, index) => {
  const totalRequests = 515 + Math.round(Math.sin(index / 2.7) * 92) + Math.round(index * 2.3);
  const identified = Math.round(totalRequests * (.46 + index / 4500));
  const candidates = Math.round(totalRequests * .155);
  return {
    date: new Date(demoSeriesStart.getTime() + index * 86_400_000).toISOString().slice(0, 10),
    totalRequests,
    identified,
    candidates,
    unknown: Math.max(0, totalRequests - identified - candidates)
  };
});

export const demoStatistics: DashboardStatistics = {
  period: { days: 30, from: demoStatisticsFrom.toISOString(), to: demoStatisticsTo.toISOString(), dataSource: "demo" },
  totalRequests: 18492,
  identified: 9217,
  recognizedCompanies: 1745,
  candidates: 2864,
  unknown: 6411,
  suppressed: 1743,
  resolverErrors: 92,
  consentedVisitors: 1732,
  reveals: 318,
  precision: 0.987,
  consentRate: 0.314,
  liveAccounts: 3,
  newCompanies: 603,
  returningCompanies: 1142,
  newVisitors: 498,
  returningVisitors: 1234,
  conversions: 428,
  conversionRate: .032,
  averageEngagementMs: 84_200,
  previous: { totalRequests: 16452, identified: 8011, reveals: 289, consentRate: .331 },
  changes: { totalRequests: .124, identified: .151, reveals: .1, consentRate: -.017 },
  timeSeries: demoTimeSeries.slice(-30),
  topCategories: [
    { label: "Enterprise Lösungen", value: 78, count: 3481 },
    { label: "Preise", value: 64, count: 2858 },
    { label: "Integrationen", value: 53, count: 2367 },
    { label: "Security", value: 42, count: 1876 },
    { label: "API", value: 31, count: 1385 }
  ],
  topCompanies: [
    { organizationId: "siemens", label: "Siemens AG", domain: "siemens.com", value: 100, count: 428 },
    { organizationId: "palfinger", label: "PALFINGER AG", domain: "palfinger.com", value: 82, count: 351 },
    { organizationId: "voestalpine", label: "voestalpine AG", domain: "voestalpine.com", value: 67, count: 287 },
    { organizationId: "omv", label: "OMV AG", domain: "omv.com", value: 54, count: 231 }
  ],
  topRegions: [
    { label: "AT", value: 100, count: 4281 },
    { label: "DE", value: 83, count: 3564 },
    { label: "CH", value: 38, count: 1621 },
    { label: "NL", value: 22, count: 948 }
  ],
  deviceBreakdown: [
    { label: "Desktop", value: 0.64, count: 1109, color: "#2757d6" },
    { label: "Mobil", value: 0.29, count: 502, color: "#7d5ce7" },
    { label: "Tablet", value: 0.07, count: 121, color: "#25a17b" }
  ],
  acquisitionBreakdown: [
    { label: "Direkt", value: 0.41, count: 710, color: "#2757d6" },
    { label: "Organic Search", value: 0.27, count: 468, color: "#25a17b" },
    { label: "Referral", value: 0.18, count: 312, color: "#e3a12f" },
    { label: "Paid / Social", value: 0.14, count: 242, color: "#9d65e5" }
  ],
  journeyFlows: [
    { source: "Startseite", target: "Lösungen", count: 612, value: 100 },
    { source: "Lösungen", target: "Preise", count: 438, value: 72 },
    { source: "Preise", target: "Kontakt", count: 246, value: 40 },
    { source: "Blog", target: "Lösungen", count: 184, value: 30 },
    { source: "Integrationen", target: "Kontakt", count: 139, value: 23 }
  ],
  consentBreakdown: [
    { label: "Akzeptiert", value: 0.314, count: 1732, color: "#128467" },
    { label: "Keine Einwilligung", value: 0.572, count: 3155, color: "#9aa5b1" },
    { label: "Unbekannt", value: 0.081, count: 447, color: "#d9a441" },
    { label: "Widerrufen", value: 0.033, count: 182, color: "#c56a42" }
  ],
  pageAnalytics: [
    { path: "/solutions", title: "Lösungen", views: 1248, uniqueVisitors: 816, totalEngagementMs: 137_280_000, averageEngagementMs: 110_000, averageScrollDepth: 72, conversionAssists: 146, sessionIds: ["demo-solutions-1", "demo-solutions-2", "demo-solutions-3"], companyTypes: [{ label: "Enterprise", count: 534 }, { label: "Mittelstand", count: 421 }, { label: "Anonyme Besucher", count: 293 }] },
    { path: "/pricing/enterprise", title: "Enterprise Pricing", views: 986, uniqueVisitors: 642, totalEngagementMs: 152_830_000, averageEngagementMs: 155_000, averageScrollDepth: 81, conversionAssists: 214, sessionIds: ["demo-pricing-1", "demo-pricing-2", "demo-pricing-3"], companyTypes: [{ label: "Enterprise", count: 491 }, { label: "Mittelstand", count: 318 }, { label: "Anonyme Besucher", count: 177 }] },
    { path: "/integrations", title: "Integrationen", views: 782, uniqueVisitors: 519, totalEngagementMs: 97_750_000, averageEngagementMs: 125_000, averageScrollDepth: 67, conversionAssists: 108, sessionIds: ["demo-integrations-1", "demo-integrations-2"], companyTypes: [{ label: "Enterprise", count: 326 }, { label: "Mittelstand", count: 284 }, { label: "Kleinunternehmen", count: 102 }, { label: "Anonyme Besucher", count: 70 }] },
    { path: "/security", title: "Security & Datenschutz", views: 554, uniqueVisitors: 398, totalEngagementMs: 60_390_000, averageEngagementMs: 109_000, averageScrollDepth: 76, conversionAssists: 72, sessionIds: ["demo-security-1", "demo-security-2"], companyTypes: [{ label: "Enterprise", count: 248 }, { label: "Mittelstand", count: 197 }, { label: "Anonyme Besucher", count: 109 }] },
    { path: "/case-studies", title: "Case Studies", views: 437, uniqueVisitors: 331, totalEngagementMs: 54_190_000, averageEngagementMs: 124_000, averageScrollDepth: 64, conversionAssists: 51, sessionIds: ["demo-cases-1"], companyTypes: [{ label: "Enterprise", count: 176 }, { label: "Mittelstand", count: 153 }, { label: "Anonyme Besucher", count: 108 }] },
    { path: "/contact", title: "Kontakt", views: 318, uniqueVisitors: 284, totalEngagementMs: 22_260_000, averageEngagementMs: 70_000, averageScrollDepth: 89, conversionAssists: 188, sessionIds: ["demo-contact-1", "demo-contact-2"], companyTypes: [{ label: "Enterprise", count: 142 }, { label: "Mittelstand", count: 101 }, { label: "Anonyme Besucher", count: 75 }] }
  ]
};
